import { useMutation, useQuery } from '@apollo/client';
import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import AgentFilterBar from './AgentFilterBar.tsx';
import AssistantActivityToolbar from './AssistantActivityToolbar.tsx';
import Alert from '../../../../core/components/Alert.tsx';
import ConfirmModal from '../../../../core/components/ConfirmModal';
import Page from '../../../../core/components/Page';
import { useToaster } from '../../../../core/components/Toast.tsx';
import { useApplicationContext } from '../../../../core/contexts/ApplicationContext.tsx';
import { buildFilterQuery, SEARCH_FILTER_KEY, useUserFilters } from '../../../../core/utils/filterQuery';
import { useControllableState } from '../../../../core/utils/useControllableState.tsx';
import useSearchParamState from '../../../../core/utils/useSearchParamState';
import { canSeeTestingDocuments } from '../../../../users/utils.ts';
import {
    DELETE_AGENT_PROCESSING_RECORD,
    GET_AGENT_PROCESSING_RECORD,
    GET_AGENT_PROCESSING_RECORDS,
    REPROCESS_AGENT_PROCESSING_RECORD,
    RETRY_AGENT_PROCESSING_RECORD_STEP_RUN,
    REUPLOAD_AGENT_PROCESSING_RECORD_FOR_TESTING,
} from '../../queries.ts';
import ProcessingRecordModal, { parseJsonSafe } from '../records/ProcessingRecordModal';
import ProcessingRecordsTable from '../records/ProcessingRecordsTable';

const PAGE_PARAM = 'activityPage';
const ITEMS_PER_PAGE_PARAM = 'activityPageSize';
const DEFAULT_ITEMS_PER_PAGE = 10;

interface AgentActivityProps {
    agentId: string;
    storageKey: string;
    emptyState: React.ReactNode;
    toolbar?: React.ReactNode;
    filtersInline?: boolean;
    selectedRecordId?: string | null;
    onSelectedRecordChange?: (recordId: string | null) => void;
}

const AgentActivity = ({
    agentId,
    storageKey,
    emptyState,
    toolbar,
    filtersInline = false,
    selectedRecordId: propsSelectedRecordId,
    onSelectedRecordChange,
}: AgentActivityProps) => {
    const { t } = useTranslation('agents');
    const { t: tAssistants } = useTranslation('assistants');
    const { user } = useApplicationContext();
    const { publishToast } = useToaster();

    const [activePage, setActivePage] = useSearchParamState(PAGE_PARAM, '1');
    const [itemsPerPage, setItemsPerPage] = useSearchParamState(
        ITEMS_PER_PAGE_PARAM,
        DEFAULT_ITEMS_PER_PAGE.toString()
    );
    const [selectedRecordId, setSelectedRecordId] = useControllableState<string | null>(
        null,
        propsSelectedRecordId,
        onSelectedRecordChange
    );
    const [recordToDelete, setRecordToDelete] = useState<any>(null);

    const parsedActivePage = parseInt(activePage || '1', 10);
    const parsedItemsPerPage = parseInt(itemsPerPage || DEFAULT_ITEMS_PER_PAGE.toString(), 10);

    const [userFilters, setUserFilters] = useUserFilters({
        storageKey,
        shouldReadSearchParams: true,
        shouldWriteSearchParams: true,
        shouldReadLocalStorage: true,
        shouldWriteLocalStorage: true,
        defaultPageSize: DEFAULT_ITEMS_PER_PAGE,
    });

    const searchValue = userFilters.find((f) => f.name === SEARCH_FILTER_KEY)?.value;
    const nonSearchFilters = userFilters.filter((f) => f.name !== SEARCH_FILTER_KEY);

    const filtersKey = buildFilterQuery(userFilters);
    const previousFiltersKeyRef = useRef(filtersKey);
    useEffect(() => {
        if (previousFiltersKeyRef.current !== filtersKey) {
            previousFiltersKeyRef.current = filtersKey;
            setActivePage('1');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filtersKey]);

    const {
        data: recordsData,
        loading: recordsLoading,
        error: recordsError,
        refetch: refetchRecords,
    } = useQuery(GET_AGENT_PROCESSING_RECORDS, {
        fetchPolicy: 'cache-and-network',
        notifyOnNetworkStatusChange: true,
        variables: {
            agentId,
            offset: (parsedActivePage - 1) * parsedItemsPerPage,
            limit: parsedItemsPerPage,
            isTesting: false,
            search: Array.isArray(searchValue) ? searchValue[0] : searchValue,
            filters: nonSearchFilters,
        },
    });

    const { data: selectedRecordData } = useQuery(GET_AGENT_PROCESSING_RECORD, {
        variables: { id: selectedRecordId },
        skip: !selectedRecordId,
    });
    const selectedRecord = selectedRecordData?.agentProcessingRecord;

    const [deleteProcessingRecord] = useMutation(DELETE_AGENT_PROCESSING_RECORD, {
        onCompleted: () => {
            publishToast({ description: t('historyPage.deleteSuccess'), status: 'success' });
            refetchRecords();
            setRecordToDelete(null);
        },
        onError: (error) => {
            publishToast({ description: t('historyPage.deleteError', { error: error.message }), status: 'error' });
        },
    });

    const [retryProcessingRecordStepRun] = useMutation(RETRY_AGENT_PROCESSING_RECORD_STEP_RUN, {
        onCompleted: () => {
            publishToast({ description: t('historyPage.retrySuccess'), status: 'success' });
            refetchRecords();
        },
        onError: (error) => {
            publishToast({ description: t('historyPage.retryError', { error: error.message }), status: 'error' });
        },
    });

    const [reprocessProcessingRecord] = useMutation(REPROCESS_AGENT_PROCESSING_RECORD, {
        onCompleted: () => {
            publishToast({ description: t('historyPage.reprocessSuccess'), status: 'success' });
            refetchRecords();
        },
        onError: (error) => {
            publishToast({ description: t('historyPage.reprocessError', { error: error.message }), status: 'error' });
        },
    });

    const [reuploadRecordForTesting] = useMutation(REUPLOAD_AGENT_PROCESSING_RECORD_FOR_TESTING, {
        onCompleted: () => {
            publishToast({ description: t('historyPage.reuploadForTestingSuccess'), status: 'success' });
        },
        onError: (error) => {
            publishToast({
                description: t('historyPage.reuploadForTestingError', { error: error.message }),
                status: 'error',
            });
        },
    });

    const processingRecords = recordsData?.filteredAgentProcessingRecordsList?.records || [];
    const totalCount = recordsData?.filteredAgentProcessingRecordsList?.totalCount ?? 0;

    const [knownCreatorsByEmail, setKnownCreatorsByEmail] = useState<Record<string, any>>({});
    useEffect(() => {
        const newCreators = processingRecords.filter(
            (record: any) => record.createdBy && !knownCreatorsByEmail[record.createdBy.email]
        );
        if (newCreators.length === 0) return;
        setKnownCreatorsByEmail((prev) => {
            const next = { ...prev };
            newCreators.forEach((r: any) => {
                next[r.createdBy.email] = r.createdBy;
            });
            return next;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [processingRecords]);
    const knownCreators = Object.values(knownCreatorsByEmail);

    const [knownSenders, setKnownSenders] = useState<Set<string>>(new Set());
    useEffect(() => {
        const newSenders = processingRecords
            .map((r: any) => parseJsonSafe(r.inputData)?.sender)
            .filter((sender: string | undefined): sender is string => !!sender && !knownSenders.has(sender));
        if (newSenders.length === 0) return;
        setKnownSenders((prev) => new Set([...prev, ...newSenders]));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [processingRecords]);

    const isInternalUser = canSeeTestingDocuments(user);

    const handleRetryRecord = async (record: any) => {
        await retryProcessingRecordStepRun({ variables: { recordId: record.id, force: false } });
    };
    const handleReprocessRecord = async (record: any) => {
        await reprocessProcessingRecord({ variables: { recordId: record.id } });
    };
    const handleReuploadForTesting = async (record: any) => {
        await reuploadRecordForTesting({ variables: { recordId: record.id } });
    };
    const handleConfirmDeleteRecord = async () => {
        if (!recordToDelete) return;
        await deleteProcessingRecord({ variables: { id: recordToDelete.id } });
    };

    const showsEmptyState = !recordsLoading && processingRecords.length === 0 && userFilters.length === 0;

    return (
        <>
            {!filtersInline && (
                <AgentFilterBar
                    filters={userFilters}
                    onFiltersChange={setUserFilters}
                    creators={knownCreators}
                    senders={Array.from(knownSenders)}
                    tabs={toolbar}
                />
            )}

            <Page.Content lowered className="flex flex-col gap-4">
                {filtersInline && !showsEmptyState && (
                    <AssistantActivityToolbar
                        filters={userFilters}
                        onFiltersChange={setUserFilters}
                        creators={knownCreators}
                        senders={Array.from(knownSenders)}
                    />
                )}
                {/* a failed load isn't an empty list, so it never shows the empty state */}
                {recordsError && !recordsLoading ? (
                    <Alert severity="error">{tAssistants('detail.activity.loadError')}</Alert>
                ) : showsEmptyState ? (
                    emptyState
                ) : (
                    <ProcessingRecordsTable
                        records={processingRecords}
                        loading={recordsLoading}
                        totalCount={totalCount}
                        activePage={parsedActivePage}
                        itemsPerPage={parsedItemsPerPage}
                        setActivePage={(page: number) => setActivePage(page ? page.toString() : '')}
                        setItemsPerPage={(size: number) => setItemsPerPage(size ? size.toString() : '')}
                        onRowClick={(record) => setSelectedRecordId(record.id)}
                        onDelete={setRecordToDelete}
                        onRetry={handleRetryRecord}
                        onReprocess={handleReprocessRecord}
                        onReuploadForTesting={isInternalUser ? handleReuploadForTesting : undefined}
                    />
                )}

                {selectedRecord && (
                    <ProcessingRecordModal
                        visible
                        onClose={() => setSelectedRecordId(null)}
                        record={selectedRecord}
                        isTesting={false}
                    />
                )}

                {recordToDelete && (
                    <ConfirmModal
                        visible
                        onClose={() => setRecordToDelete(null)}
                        onCancel={() => setRecordToDelete(null)}
                        onConfirm={handleConfirmDeleteRecord}
                        title={t('historyPage.deleteRecordConfirmTitle')}
                        labelConfirm={t('historyPage.deleteRecordConfirm')}
                        danger
                    >
                        {t('historyPage.deleteRecordConfirmMessage')}
                    </ConfirmModal>
                )}
            </Page.Content>
        </>
    );
};

export default AgentActivity;
